import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

import Button from "./Button";
import Input from "./Input";
import Select from "./Select";

/**
 * Reusable bulk-edit overlay. Accepts an arbitrary list of field
 * definitions and renders the matching control for each one. A field is
 * only included in the submitted values object when it has a non-empty
 * value, letting callers update one or many fields per pass.
 *
 * Field definition shape:
 *   {
 *     id: string,             // key in the submitted values object
 *     label: string,          // visible label
 *     type: "select" | "text" | "number" | "date",
 *     options?: [{ value, label }],  // required for type "select"
 *     placeholder?: string,
 *     defaultValue?: any,
 *     helperText?: string,
 *   }
 */
const buildInitialState = (fields) =>
    fields.reduce((acc, field) => {
        acc[field.id] = field.defaultValue != null ? field.defaultValue : "";
        return acc;
    }, {});

const renderControl = (field, value, onChange, disabled) => {
    const commonProps = {
        id: `bulk-edit-${field.id}`,
        value,
        disabled,
        onChange: (e) => onChange(e.target.value),
    };

    if (field.type === "select") {
        return (
            <Select {...commonProps}>
                <option value="">{field.placeholder || "Select a value"}</option>
                {(field.options || []).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label}
                    </option>
                ))}
            </Select>
        );
    }

    return (
        <Input
            {...commonProps}
            type={field.type || "text"}
            placeholder={field.placeholder || ""}
        />
    );
};

const BulkEditDialog = ({
    open,
    fields,
    onSubmit,
    onCancel,
    title = "Bulk edit",
    description,
    submitLabel = "Update",
    cancelLabel = "Cancel",
    selectionCount = 0,
}) => {
    const [values, setValues] = useState(() => buildInitialState(fields));
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            setValues(buildInitialState(fields));
            setError(null);
            setSubmitting(false);
        }
    }, [open, fields]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape" && !submitting) onCancel();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onCancel, submitting]);

    const populatedValues = useMemo(() => {
        const out = {};
        fields.forEach((field) => {
            const v = values[field.id];
            if (v !== "" && v != null) out[field.id] = v;
        });
        return out;
    }, [values, fields]);

    const setFieldValue = (id, value) =>
        setValues((prev) => ({
            ...prev,
            [id]: value,
        }));

    const handleSubmit = async () => {
        if (Object.keys(populatedValues).length === 0) {
            setError("Set a value on at least one field to update.");
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            await onSubmit(populatedValues);
        } catch (e) {
            setError(e?.message || "Something went wrong. Please contact support.");
            setSubmitting(false);
        }
    };

    if (!open) return null;

    const dialog = (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="bulk-edit-title"
            className="fixed inset-0 z-[10000] flex items-center justify-center px-4 isolate"
        >
            <button
                type="button"
                aria-label="Cancel"
                onClick={submitting ? undefined : onCancel}
                className="absolute inset-0 z-0 bg-ink-900 bg-opacity-50"
            />
            <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-elevated">
                <div className="flex items-start justify-between">
                    <div>
                        <h3 id="bulk-edit-title" className="text-lg font-semibold text-ink-900">
                            {title}
                        </h3>
                        {description && (
                            <p className="mt-1 text-sm text-ink-600">{description}</p>
                        )}
                        {selectionCount > 0 && (
                            <p className="mt-1 text-xs font-medium text-ink-500">
                                Applying to {selectionCount} record
                                {selectionCount === 1 ? "" : "s"}.
                            </p>
                        )}
                    </div>
                </div>

                <div className="mt-5 space-y-4">
                    {fields.map((field) => (
                        <div key={field.id}>
                            <label
                                htmlFor={`bulk-edit-${field.id}`}
                                className="mb-1.5 block text-sm font-medium text-ink-700"
                            >
                                {field.label}
                            </label>
                            {renderControl(
                                field,
                                values[field.id] ?? "",
                                (value) => setFieldValue(field.id, value),
                                submitting,
                            )}
                            {field.helperText && (
                                <p className="mt-1 text-xs text-ink-500">{field.helperText}</p>
                            )}
                        </div>
                    ))}
                </div>

                {error && (
                    <p role="alert" className="mt-4 text-sm text-danger-600">
                        {error}
                    </p>
                )}

                <div className="mt-6 flex justify-end gap-2">
                    <Button variant="secondary" onClick={onCancel} disabled={submitting}>
                        {cancelLabel}
                    </Button>
                    <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
                        {submitting ? "Updating..." : submitLabel}
                    </Button>
                </div>
            </div>
        </div>
    );

    return createPortal(dialog, document.body);
};

BulkEditDialog.propTypes = {
    open: PropTypes.bool,
    fields: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            type: PropTypes.oneOf(["select", "text", "number", "date"]),
            options: PropTypes.arrayOf(
                PropTypes.shape({
                    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                    label: PropTypes.string.isRequired,
                }),
            ),
            placeholder: PropTypes.string,
            defaultValue: PropTypes.any,
            helperText: PropTypes.string,
        }),
    ).isRequired,
    onSubmit: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    title: PropTypes.string,
    description: PropTypes.node,
    submitLabel: PropTypes.string,
    cancelLabel: PropTypes.string,
    selectionCount: PropTypes.number,
};

export default BulkEditDialog;
